import React, { useState } from "react";
import { motion } from "framer-motion";
import { useGameStateContext } from "../../GameStateProvider";
import { useShopActions } from "../../hooks/useShopActions";
import { CONSTANTS } from "../../data";
import { ASSETS } from "../../assetManifest.js";
import { Store, X, ShoppingCart, Book, Zap, Wrench, Sparkles } from "lucide-react";

interface ShopModalProps {
  show: boolean;
  onClose: () => void;
}

const ShopModal: React.FC<ShopModalProps> = ({ show, onClose }) => {
  const { state } = useGameStateContext();
  const { handlePurchaseItem, handlePurchaseRecipe, handleUpgradeAiBarista, handleUpgradeEquipment, handlePurchaseDecoration } = useShopActions();
  const [activeTab, setActiveTab] = useState<"ingredients" | "recipes" | "upgrades" | "decorations">("ingredients");

  if (!show) return null;

  const { coins, level, prestigeLevel, inventory, activeSeason, brewStations, discoveredRecipes, aiBarista, equipmentLevels } = state;
  const decorations = (state as any).decorations || {};
  const decorationEntries = Object.entries((CONSTANTS as any).DECORATION_SHOP_ITEMS || {});
  const decorationEffects = (CONSTANTS as any).DECORATION_EFFECTS || {};

  const renderIngredientsTab = () => (
    <div className="space-y-3">
      {Object.entries(CONSTANTS.SHOP_ITEMS).map(([itemId, shopItem]) => {
        const item = CONSTANTS.ITEMS[itemId];
        const isLevelUnlocked = !shopItem.unlockLevel || shopItem.unlockLevel <= level;
        const isPrestigeUnlocked = !shopItem.prestige || shopItem.prestige <= prestigeLevel;
        const isSeasonallyAvailable = !item.season || (activeSeason && item.season === activeSeason.type);

        const isUnlocked = isLevelUnlocked && isSeasonallyAvailable && isPrestigeUnlocked;

        if (!isUnlocked) return null;

        const sprite = (ASSETS as any).items?.[itemId]?.path || (ASSETS as any).images?.defaultItem?.path;

        if (itemId === "brew_station_2") {
          const hasStation = brewStations.length >= 2;
          const canAfford = coins >= shopItem.price;
          return (
            <div key={itemId} className="shop-item bg-yellow-50 p-3 sm:p-4 rounded-lg border border-yellow-300">
              <div className="flex items-start gap-3">
                <img src={sprite} alt={item.name} className="w-12 h-12 sm:w-16 sm:h-16 object-contain bg-white/10 rounded-md p-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-text-primary text-sm sm:text-base">{item.name}</div>
                      <div className="text-xs sm:text-sm text-text-secondary">{item.description}</div>
                      <div className="text-sm font-bold text-success">💰 {shopItem.price} coins</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-text-secondary">Max: 1</div>
                      <div className="text-xs text-text-secondary">Have: {brewStations.length - 1}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => handlePurchaseItem(itemId, 1)}
                    disabled={hasStation || !canAfford}
                    className="w-full bg-success text-white px-3 py-3 rounded text-sm font-bold hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed min-h-[44px]"
                  >
                    {hasStation ? "Purchased" : "Buy"}
                  </button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div key={itemId} className="shop-item bg-bg-main p-3 sm:p-4 rounded-lg border border-border-color">
            <div className="flex items-start gap-3">
              <img src={sprite} alt={item.name} className="w-12 h-12 sm:w-16 sm:h-16 object-contain bg-white/10 rounded-md p-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-text-primary text-sm sm:text-base">{item.name}</div>
                    <div className="text-xs sm:text-sm text-text-secondary">{item.description}</div>
                    <div className="text-sm font-bold text-success">💰 {shopItem.price} coins</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs text-text-secondary">Max: {shopItem.maxPurchase}</div>
                    <div className="text-xs text-text-secondary">Have: {inventory[itemId] || 0}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1 sm:gap-2">
                  {[1, 5, 10].map((qty) => {
                    const totalCost = shopItem.price * qty;
                    const canAffordQty = coins >= totalCost;
                    const withinLimit = (inventory[itemId] || 0) + qty <= shopItem.maxPurchase;

                    return (
                      <button
                        key={qty}
                        onClick={() => handlePurchaseItem(itemId, qty)}
                        disabled={!canAffordQty || !withinLimit}
                        className="bg-success text-white px-2 py-2 rounded text-xs sm:text-sm hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center"
                      >
                        <span className="text-center">+{qty}<br />({totalCost}💰)</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderRecipesTab = () => (
    <div className="space-y-3">
      {Object.entries(CONSTANTS.RECIPE_SHOP_ITEMS).map(([recipeId, recipeShopItem]) => {
        const recipe = CONSTANTS.RECIPES[recipeId];
        if (!recipe) return null;

        const isLevelUnlocked = !recipeShopItem.unlockLevel || recipeShopItem.unlockLevel <= level;
        const isPrestigeUnlocked = !recipeShopItem.prestige || recipeShopItem.prestige <= prestigeLevel;
        const isAlreadyDiscovered = discoveredRecipes.includes(recipeId);
        const canAfford = coins >= recipeShopItem.price;

        const isUnlocked = isLevelUnlocked && isPrestigeUnlocked;

        if (!isUnlocked) return null;

        const resultSprite = (ASSETS as any).items?.[recipe.result.id]?.path || (ASSETS as any).images?.defaultItem?.path;

        return (
          <div
            key={recipeId}
            className={`shop-item p-3 sm:p-4 rounded-lg border ${isAlreadyDiscovered ? "bg-green-50 border-success/30" : "bg-bg-main border-border-color"}`}
          >
            <div className="flex items-start gap-3">
              <img src={resultSprite} alt={recipe.result.name} className="w-12 h-12 sm:w-16 sm:h-16 object-contain bg-white/10 rounded-md p-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-text-primary text-sm sm:text-base">{recipeShopItem.name}</div>
                    <div className="text-xs sm:text-sm text-text-secondary break-words">
                      {recipe.ingredients.map((ing) => CONSTANTS.ITEMS[ing]?.name || ing).join(" + ")} → {recipe.result.name}
                    </div>
                    <div className="text-sm font-bold text-success">💰 {recipeShopItem.price} coins</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {isAlreadyDiscovered && <div className="text-xs text-success font-bold">✓ Known</div>}
                  </div>
                </div>
                <button
                  onClick={() => handlePurchaseRecipe(recipeId)}
                  disabled={isAlreadyDiscovered || !canAfford}
                  className="w-full bg-primary-action text-white px-3 py-3 rounded text-sm font-bold hover:bg-accent transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {isAlreadyDiscovered ? "Already Known" : `Learn Recipe (${recipeShopItem.price}💰)`}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderUpgradesTab = () => {
    const currentAiLevel = aiBarista.level;
    const isAiUnlocked = currentAiLevel > 0;
    const maxAiLevel = Object.keys(CONSTANTS.AI_BARISTA.LEVELS).length - 1;
    const isAiMaxLevel = currentAiLevel >= maxAiLevel;

    let aiTitle: string;
    let aiDescription: string;
    let aiCost: number | undefined;
    let canAffordAi = false;
    let aiButtonText: string;
    let isAiLocked: boolean;

    if (!isAiUnlocked) {
      aiTitle = "Purchase AI Barista";
      aiDescription = "Automate your cafe! The AI Barista will automatically craft and serve drinks for customers.";
      aiCost = CONSTANTS.AI_BARISTA.UNLOCK_COST;
      isAiLocked = level < CONSTANTS.AI_BARISTA.UNLOCK_LEVEL;
      canAffordAi = coins >= aiCost && !isAiLocked;
      aiButtonText = isAiLocked ? `Requires Level ${CONSTANTS.AI_BARISTA.UNLOCK_LEVEL}` : `Buy for ${aiCost}💰`;
    } else if (!isAiMaxLevel) {
      const nextLevelData = CONSTANTS.AI_BARISTA.LEVELS[currentAiLevel + 1];
      aiTitle = `Upgrade AI Barista to Level ${currentAiLevel + 1}`;
      aiDescription = `Increase speed and efficiency. Speed: ${aiBarista.speed}ms -> ${nextLevelData.speed}ms. Efficiency: ${Math.round(
        aiBarista.efficiency * 100
      )}% -> ${Math.round(nextLevelData.efficiency * 100)}%.`;
      aiCost = nextLevelData.cost;
      canAffordAi = coins >= aiCost;
      aiButtonText = `Upgrade for ${aiCost}💰`;
    } else {
      aiTitle = "AI Barista Max Level";
      aiDescription = `Your AI Barista is fully upgraded! Speed: ${aiBarista.speed}ms. Efficiency: ${Math.round(aiBarista.efficiency * 100)}%.`;
    }

    const renderEquipmentUpgrade = (equipmentId: "grinder" | "espresso_machine") => {
      const equipment = CONSTANTS.EQUIPMENT_UPGRADES[equipmentId];
      const currentLevel = equipmentLevels[equipmentId];
      const nextLevel = currentLevel + 1;
      const upgradeInfo = (equipment.levels as any)[nextLevel];
      const isMaxLevel = !upgradeInfo;

      let title: string;
      let description: string;
      let cost: number | undefined;
      let canAfford = false;
      let buttonText: string;
      let isLocked: boolean;

      if (isMaxLevel) {
        title = `${equipment.name} (Max Level)`;
        description = `Your ${equipment.name.toLowerCase()} is fully upgraded.`;
      } else {
        title = `Upgrade ${equipment.name} to Level ${nextLevel}`;
        description = upgradeInfo.description;
        cost = upgradeInfo.cost;
        isLocked = level < upgradeInfo.unlockLevel;
        canAfford = coins >= cost && !isLocked;
        buttonText = isLocked ? `Requires Level ${upgradeInfo.unlockLevel}` : `Upgrade for ${cost}💰`;
      }

      return (
        <div key={equipmentId} className={`shop-item p-3 sm:p-4 rounded-lg border ${isMaxLevel ? "bg-green-50 border-success/30" : "bg-bg-main border-border-color"}`}>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-action/10 rounded-md flex items-center justify-center flex-shrink-0">
              <Wrench size={24} className="text-primary-action" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-text-primary text-sm sm:text-base">{title}</div>
              <div className="text-xs sm:text-sm text-text-secondary mb-2 break-words">{description}</div>
              {!isMaxLevel && (
                <>
                  <div className="text-sm font-bold text-success">💰 {cost} coins</div>
                  <button
                    onClick={() => handleUpgradeEquipment(equipmentId)}
                    disabled={!canAfford}
                    className="w-full mt-2 bg-primary-action text-white px-3 py-3 rounded text-sm font-bold hover:bg-accent transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed min-h-[44px]"
                  >
                    {buttonText}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-4">
        <div className={`shop-item p-3 sm:p-4 rounded-lg border ${isAiMaxLevel ? "bg-green-50 border-success/30" : "bg-bg-main border-border-color"}`}>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-action/10 rounded-md flex items-center justify-center flex-shrink-0">
              <Zap size={24} className="text-primary-action" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-text-primary text-sm sm:text-base">{aiTitle}</div>
              <div className="text-xs sm:text-sm text-text-secondary mb-2 break-words">{aiDescription}</div>
              {!isAiMaxLevel && (
                <>
                  <div className="text-sm font-bold text-success">💰 {aiCost} coins</div>
                  <button
                    onClick={handleUpgradeAiBarista}
                    disabled={!canAffordAi}
                    className="w-full mt-2 bg-primary-action text-white px-3 py-3 rounded text-sm font-bold hover:bg-accent transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed min-h-[44px]"
                  >
                    {aiButtonText}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        {renderEquipmentUpgrade("grinder")}
        {renderEquipmentUpgrade("espresso_machine")}
      </div>
    );
  };

  const describeEffects = (effects: any) => {
    const parts: string[] = [];
    if (effects.tipBonus) {
      parts.push(`+${Math.round(effects.tipBonus * 100)}% tips`);
    }
    if (effects.patienceBonus) {
      parts.push(`+${effects.patienceBonus}s patience`);
    }
    if (effects.spawnRateMultiplier && effects.spawnRateMultiplier < 1) {
      parts.push(`+${Math.round((1 - effects.spawnRateMultiplier) * 100)}% faster customer flow`);
    }
    if (effects.xpBonusPercent) {
      parts.push(`+${Math.round(effects.xpBonusPercent * 100)}% XP`);
    }
    if (parts.length === 0) {
      parts.push("Minor ambience boost");
    }
    return parts.join(" • ");
  };

  const renderDecorationsTab = () => (
    <div className="space-y-3">
      {decorationEntries.length === 0 && (
        <div className="bg-bg-main p-3 sm:p-4 rounded-lg border border-border-color text-text-secondary text-sm text-center">
          No decorations available.
        </div>
      )}
      {decorationEntries.map(([decId, deco]: any) => {
        const purchased = !!decorations?.[decId];
        const canAfford = coins >= deco.price;
        const isUnlocked = !deco.unlockLevel || deco.unlockLevel <= level;
        const effects = decorationEffects[decId] || {};
        return (
          <div key={decId} className={`shop-item p-3 sm:p-4 rounded-lg border ${purchased ? "bg-green-50 border-success/30" : "bg-bg-main border-border-color"}`}>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-action/10 rounded-md flex items-center justify-center flex-shrink-0">
                <Sparkles size={24} className="text-primary-action" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-text-primary text-sm sm:text-base">{deco.name}</div>
                    <div className="text-xs sm:text-sm text-text-secondary break-words">{describeEffects(effects)}</div>
                    <div className="text-sm font-bold text-success">💰 {deco.price} coins</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs text-text-secondary">Max: 1</div>
                    <div className="text-xs text-text-secondary">Owned: {purchased ? 1 : 0}</div>
                    {!isUnlocked && <div className="text-xs text-text-secondary mt-1">Req Lvl {deco.unlockLevel}</div>}
                  </div>
                </div>
                <button
                  onClick={() => handlePurchaseDecoration(decId)}
                  disabled={purchased || !canAfford || !isUnlocked}
                  className="w-full bg-primary-action text-white px-3 py-3 rounded text-sm font-bold hover:bg-accent transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {purchased ? "Purchased" : isUnlocked ? `Buy (${deco.price}💰)` : `Requires Level ${deco.unlockLevel}`}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const tabs = [
    { id: "ingredients", name: "Ingredients", icon: <ShoppingCart size={16} /> },
    { id: "recipes", name: "Recipe Hints", icon: <Book size={16} /> },
    { id: "upgrades", name: "Upgrades", icon: <Zap size={16} /> },
    { id: "decorations", name: "Decorations", icon: <Sparkles size={16} /> },
  ];

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }} 
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-2 sm:p-4 z-50" 
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-surface rounded-lg w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col shadow-xl border border-border-color"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 sm:p-4 border-b border-border-color flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-text-primary flex items-center gap-2">
            <Store /> Shop
          </h2>
          <button 
            onClick={onClose} 
            className="text-text-secondary hover:text-text-primary p-2 rounded-full hover:bg-bg-main min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex border-b border-border-color overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-shrink-0 py-3 px-3 sm:px-4 font-semibold text-xs sm:text-sm flex items-center justify-center gap-2 transition-colors relative min-h-[44px] ${
                activeTab === tab.id ? "text-primary-action" : "text-text-secondary hover:text-text-primary hover:bg-bg-main"
              }`}
            >
              {tab.icon} <span className="hidden sm:inline">{tab.name}</span>
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-action"></div>}
            </button>
          ))}
        </div>

        <div className="modal-content p-3 sm:p-4 overflow-y-auto flex-1">
          {activeTab === "ingredients" && renderIngredientsTab()}
          {activeTab === "recipes" && renderRecipesTab()}
          {activeTab === "upgrades" && renderUpgradesTab()}
          {activeTab === "decorations" && renderDecorationsTab()}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ShopModal;
